import React, { useState } from "react";
import { Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import AdminRequestApiKeyCreate from "../Models/Request/AdminRequestApiKeyCreate";

/**
 * Create form for an API key. After creation the plain key is shown ONCE, it can not be retrieved again.
 * Layout follows SelectOrgPanel (select-org-panel-* classes).
 */
const ApiKeyCreatePanel = ({ onCreated, onClose }) => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();

    const [apiKey, setApiKey] = useState(new AdminRequestApiKeyCreate());
    const [fieldErrors, setFieldErrors] = useState({});
    const [displaySpinner, setDisplaySpinner] = useState(false);
    const [createdKey, setCreatedKey] = useState(null);
    const [copied, setCopied] = useState(false);

    const schema = AdminRequestApiKeyCreate.getAttributes(null, true);
    const canCreate = !!apiKey.name && apiKey.name.trim() !== "" && !displaySpinner;

    const handleChange = (e, name) => {
        const value = e && e.target ? e.target.value : e;
        setApiKey((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    const handleCreate = async () => {
        try {
            await schema.validate(apiKey, { abortEarly: false });
        } catch (err) {
            const newErrors = {};
            (err?.inner ?? []).forEach((e) => { if (e.path) newErrors[e.path] = e.message; });
            setFieldErrors(newErrors);
            return;
        }

        // The key is valid until the end of the selected day
        let expiresAt = null;
        if (apiKey.expiresAt) {
            const date = new Date(apiKey.expiresAt);
            date.setHours(23, 59, 59, 0);
            expiresAt = date.toISOString();
        }

        setDisplaySpinner(true);
        const apiCallOptions = {
            method: "post",
            url: "/api-key",
            data: {
                name: apiKey.name.trim(),
                expiresAt: expiresAt,
            },
        };

        try {
            await ApiCall(apiCallOptions, (response) => {
                setDisplaySpinner(false);
                if (response?.key) {
                    setCreatedKey(response.key);
                    onCreated?.();
                }
            }, (errorMessage) => {
                setDisplaySpinner(false);
                FlashMessage(errorMessage && errorMessage !== 'Error' ? errorMessage : t('validationMessage.sendError'), 'flash-messageError');
            });
        } catch (error) {
            setDisplaySpinner(false);
            console.log(error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(createdKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            FlashMessage(t('apiKey.copyError'), 'flash-messageError');
        }
    };

    const renderFieldError = (fieldName) => fieldErrors[fieldName]
        ? <div style={{ color: '#f56e6e', fontSize: 12, marginTop: 4 }}>{fieldErrors[fieldName]}</div>
        : null;

    // STEP 2: SHOW THE KEY ONCE
    if (createdKey) {
        return (
            <div className="select-org-panel-wrapper">
                <p className="select-org-panel-intro mb-1">
                    {t('apiKey.copyNowTitle')}
                </p>
                <p className="rpv2-date-label mb-4">
                    {t('apiKey.copyNowText')}
                </p>

                <div className="select-org-panel-selector-wrap">
                    <label htmlFor="createdApiKey" className="form-label rpv2-date-label">
                        {t('apiKey.apiKey')}
                    </label>
                    <input
                        id="createdApiKey"
                        type="text"
                        className="form-control"
                        value={createdKey}
                        readOnly
                        onFocus={(e) => e.target.select()}
                        style={{ fontFamily: 'monospace' }}
                    />
                    <p className="select-org-panel-label mt-2 mb-0">
                        {t('apiKey.headerUsage')} <code>X-Api-Key</code>
                    </p>
                </div>

                <div className="select-org-panel-buttons">
                    <button type="button" onClick={handleCopy} className="select-org-panel-btn-cancel">
                        <i className={`bx ${copied ? 'bx-check' : 'bx-copy'} align-middle pe-1`} />
                        {copied ? t('apiKey.copied') : t('apiKey.copy')}
                    </button>
                    <button type="button" onClick={onClose} className="select-org-panel-btn-confirm enabled">
                        {t('apiKey.done')}
                    </button>
                </div>
            </div>
        );
    }

    // STEP 1: FORM
    return (
        <div className="select-org-panel-wrapper">
            <p className="select-org-panel-intro mb-1">
                {t('apiKey.createIntro')}
            </p>
            <p className="rpv2-date-label mb-4">
                {t('apiKey.createHelp')}
            </p>

            <div className="mb-3 position-relative">
                <label className="form-label rpv2-date-label">{t('apiKey.name')}</label>
                <CustomInput
                    model={AdminRequestApiKeyCreate}
                    attribute="name"
                    data={apiKey.name}
                    customChange={(e) => handleChange(e, 'name')}
                    disableBlurValidation={true}
                />
                {renderFieldError('name')}
            </div>

            <div className="select-org-panel-selector-wrap position-relative">
                <label className="form-label rpv2-date-label">{t('apiKey.expiresAtOptional')}</label>
                <CustomInput
                    model={AdminRequestApiKeyCreate}
                    attribute="expiresAt"
                    data={apiKey.expiresAt}
                    customChange={(e) => handleChange(e, 'expiresAt')}
                    disableBlurValidation={true}
                />
                {renderFieldError('expiresAt')}
            </div>

            <div className="select-org-panel-buttons">
                <button type="button" onClick={onClose} className="select-org-panel-btn-cancel">
                    {t('button.cancel')}
                </button>
                <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!canCreate}
                    className={`select-org-panel-btn-confirm ${canCreate ? "enabled" : "disabled"}`}
                >
                    {displaySpinner
                        ? <Spinner size="sm" className="align-middle" />
                        : t('button.create')}
                </button>
            </div>
        </div>
    );
};

export default ApiKeyCreatePanel;
